import { AddBox, ArrowDownward, ArrowUpward, Close, DeleteForever, ExpandMore, MoreVert } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { TranslateText } from '../../../application/language/TranslateText';
import { ICommonProperties } from '../../../common/properties/ICommonProperties';
import { BlockType } from '../../../service/unit/interface/IUnitTheory';
import { IUnitDomain } from '../../admin/page/unit/store/IUnitDomain';
import { Colors } from '../color/Colors';
import { ElementFactory } from './store/elements/ElementFactory';
import { Chapter } from './store/elements/text/Chapter';
import { IConsturctorDomain } from './store/IConstructorDomain';

export interface IConstructorProperties extends ICommonProperties {
  domain: IConsturctorDomain;
  unitDomain: IUnitDomain;
}

const INITIAL_BLOCKS_RENDER_COUNT = 10;
const BLOCKS_RENDER_CHUNK_SIZE = 8;
const CHAPTERS_DROPPABLE_ID = 'chapters';
const THEORY_BLOCKS_DROPPABLE_ID = 'theory-blocks';
const COMPONENT_LIST_DROPPABLE_PREFIX = 'component-list-';

const DropItem = observer(({ provided, snapshot, domain, item, index }: any) => {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onClick={() => {
        domain.ui.activeChapter.setValue(item);
      }}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        background: item.pseudoId === domain.ui.activeChapter.value.pseudoId ? Colors.bgSurface : '',
        padding: '8px 12px',
        marginBottom: '8px',
        borderRadius: '8px',
        marginTop: '8px',
        '&:hover': { background: Colors.bgSurface },
        '&:hover .chapter__delete': { visibility: 'visible' },
      }}
    >
      <Typography>{item.title}</Typography>
      <Close
        className="chapter__delete"
        sx={{ visibility: 'hidden', cursor: 'pointer' }}
        onClick={() => domain.onDeleteChapter(index)}
      />
    </Box>
  );
});

const TheoryBlockItem = observer(
  ({
    block,
    index,
    domain,
    onToggle,
  }: {
    block: any;
    index: number;
    domain: IConsturctorDomain;
    onToggle: (next: boolean) => void;
  }) => {
    const truncateToThreeWords = (value: string): string => {
      const text = (value || '').trim().replace(/\s+/g, ' ');
      if (!text) {
        return '';
      }
      const words = text.split(' ');
      if (words.length <= 3) {
        return text;
      }
      return `${words.slice(0, 3).join(' ')}...`;
    };

    const Element = ElementFactory[block.block];
    const elementData = { data: block.data, pseudoId: block.pseudoId };
    const blockData = block?.data || {};
    const blockDescription =
      blockData?.name ||
      blockData?.title ||
      blockData?.description ||
      blockData?.originalName ||
      blockData?.text ||
      blockData?.example?.name ||
      (typeof blockData?.rowsCount === 'number' && typeof blockData?.columnsCount === 'number'
        ? `${blockData.rowsCount}x${blockData.columnsCount}`
        : '') ||
      (typeof blockData?.elementCount === 'number' ? `${blockData.elementCount} items` : '') ||
      (typeof blockData?.tabsCount === 'number' ? `${blockData.tabsCount} tabs` : '') ||
      (typeof blockData?.tagsCount === 'number' ? `${blockData.tagsCount} tags` : '');
    const localizedBlockName =
      domain.ui.componentList.list
        .flatMap((group) => group.list)
        .find((templateBlock) => templateBlock.block === block?.block)?.name ||
      block?.name ||
      block?.block ||
      'Блок';

    const blockTitle = `${localizedBlockName}${blockDescription ? `: ${truncateToThreeWords(blockDescription)}` : ''}`;

    const isSpacingBlock = block?.block === BlockType.SPACING_48 || block?.block === BlockType.SPACING_64;
    const isExpanded = isSpacingBlock ? false : Boolean(block?.isOpen);

    return (
      <Box
        sx={{ padding: '0px 24px', width: '100%', marginBottom: '12px' }}
        data-block-id={block.pseudoId}
        id={`theory-block-${block.pseudoId}`}
      >
        <Box
          sx={{
            background: Colors.bgSurface,
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              borderBottom: isExpanded ? `1px solid ${Colors.border}` : 'none',
              cursor: isSpacingBlock ? 'default' : 'pointer',
            }}
            onClick={() => {
              if (!isSpacingBlock) {
                onToggle(!isExpanded);
              }
            }}
          >
            <Typography
              sx={{
                marginRight: 'auto',
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {blockTitle}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  domain.onUp(index);
                }}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  domain.onDown(index);
                }}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: '#EF4444' }}
                onClick={(event) => {
                  event.stopPropagation();
                  domain.onDelete(index);
                }}
              >
                <DeleteForever fontSize="small" />
              </IconButton>
              {!isSpacingBlock && (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggle(!isExpanded);
                  }}
                  sx={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <ExpandMore />
                </IconButton>
              )}
              {isSpacingBlock && <Box sx={{ width: 34, height: 34 }} />}
            </Box>
          </Box>
          {isExpanded && (
            <Box
              sx={{
                paddingTop: '8px',
                '& .action': {
                  display: 'none !important',
                },
              }}
            >
              <Element
                layoutDomain={domain.layoutDomain}
                data={elementData}
                onDelete={() => {
                  domain.onDelete(index);
                }}
                onUp={() => domain.onUp(index)}
                onDown={() => domain.onDown(index)}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

export const Constructor = observer(({ domain, unitDomain }: IConstructorProperties) => {
  const [renderedBlocksCount, setRenderedBlocksCount] = useState(INITIAL_BLOCKS_RENDER_COUNT);
  const previousChapterIdRef = useRef<string>('');
  const previousBlockCountRef = useRef<number>(0);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const lastAddMethodRef = useRef<'double_click' | 'dnd_drop' | null>(null);

  const activeChapter = domain.ui.activeChapter.value;
  const activeChapterBlocks = activeChapter?.theoryBlocks || [];
  const activeChapterId = activeChapter?.pseudoId || '';

  const onDragStart = (result: any) => {
    if (result?.source?.droppableId === THEORY_BLOCKS_DROPPABLE_ID) {
      setRenderedBlocksCount(activeChapterBlocks.length);
    }
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    if (source.droppableId === CHAPTERS_DROPPABLE_ID && destination.droppableId === CHAPTERS_DROPPABLE_ID) {
      domain.reorderChapters(source.index, destination.index);
      return;
    }

    if (source.droppableId === THEORY_BLOCKS_DROPPABLE_ID && destination.droppableId === THEORY_BLOCKS_DROPPABLE_ID) {
      domain.reorderTheoryBlocks(source.index, destination.index);
      return;
    }

    if (
      source.droppableId.startsWith(COMPONENT_LIST_DROPPABLE_PREFIX) &&
      destination.droppableId === THEORY_BLOCKS_DROPPABLE_ID
    ) {
      const componentGroupIndex = Number(source.droppableId.replace(COMPONENT_LIST_DROPPABLE_PREFIX, ''));
      if (Number.isNaN(componentGroupIndex)) {
        return;
      }

      const componentGroup = domain.ui.componentList.list[componentGroupIndex];
      const componentTemplate = componentGroup?.list[source.index];
      if (!componentTemplate) {
        return;
      }

      lastAddMethodRef.current = 'dnd_drop';
      domain.onInsertComponent(componentTemplate, destination.index);
    }
  };

  useEffect(() => {
    domain.ui.activeChapter.setValue(unitDomain.ui.unit.entity.theory.chapters[0]);
  }, [domain, unitDomain]);

  useEffect(() => {
    const isChapterChanged = previousChapterIdRef.current !== activeChapterId;
    const previousBlockCount = previousBlockCountRef.current;
    const currentBlockCount = activeChapterBlocks.length;

    if (!isChapterChanged && currentBlockCount > previousBlockCount && currentBlockCount > 0) {
      const addMethod = lastAddMethodRef.current;
      setRenderedBlocksCount(currentBlockCount);

      if (addMethod === 'double_click') {
        const newBlock = activeChapterBlocks[currentBlockCount - 1];
        if (newBlock?.pseudoId) {
          newBlock.isOpen = true;
        }

        const container = contentContainerRef.current;
        if (container) {
          requestAnimationFrame(() => {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: 'smooth',
            });
          });
        }
      }

      lastAddMethodRef.current = null;
    }

    if (isChapterChanged) {
      lastAddMethodRef.current = null;
    }

    previousChapterIdRef.current = activeChapterId;
    previousBlockCountRef.current = currentBlockCount;
  }, [activeChapterBlocks.length, activeChapterId]);

  useEffect(() => {
    setRenderedBlocksCount(Math.min(INITIAL_BLOCKS_RENDER_COUNT, activeChapterBlocks.length));

    activeChapterBlocks.forEach((block) => {
      block.isOpen = false;
    });

    if (process.env.NODE_ENV === 'development' && domain.ui.activeChapter.value?.pseudoId) {
      const chapterId = domain.ui.activeChapter.value.pseudoId;
      const startMark = `chapter_switch_start_${chapterId}`;
      const endMark = `chapter_headers_rendered_${chapterId}`;
      const measureName = `chapter_switch_measure_${chapterId}`;

      performance.mark(startMark);
      requestAnimationFrame(() => {
        performance.mark(endMark);
        performance.measure(measureName, startMark, endMark);
      });
    }
  }, [activeChapterId]);

  useEffect(() => {
    if (renderedBlocksCount >= activeChapterBlocks.length) {
      return;
    }

    let isCancelled = false;
    let frameId = 0;

    const progressivelyRender = () => {
      if (isCancelled) {
        return;
      }
      let shouldContinueRendering = true;
      setRenderedBlocksCount((prev) => {
        const next = Math.min(prev + BLOCKS_RENDER_CHUNK_SIZE, activeChapterBlocks.length);
        shouldContinueRendering = next < activeChapterBlocks.length;
        return next;
      });
      if (shouldContinueRendering) {
        frameId = window.requestAnimationFrame(progressivelyRender);
      }
    };

    frameId = window.requestAnimationFrame(progressivelyRender);

    return () => {
      isCancelled = true;
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [renderedBlocksCount, activeChapterBlocks.length, activeChapterId]);

  const visibleBlocks = activeChapterBlocks.slice(0, renderedBlocksCount);
  const hasMoreBlocksToRender = renderedBlocksCount < activeChapterBlocks.length;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}></Box>
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          <Box
            ref={contentContainerRef}
            sx={{
              flex: '0.7',
              background: Colors.bgElevated,
              padding: '16px 0px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              height: '100%',
              overflowY: 'auto',
            }}
          >
            <Box sx={{ marginLeft: '24px', display: 'flex', gap: '8px', alignSelf: 'self-start' }}>
              <Button variant="contained" sx={{ padding: '5px 10px' }} size="small" onClick={() => domain.onClearAll()}>
                <TranslateText i18nKey="createUnit.clearChapter" />
              </Button>
            </Box>
            <Box sx={{ padding: '0px 24px', width: '100%' }}>
              <Chapter data={domain.ui.activeChapter.value} />
            </Box>
            <Droppable droppableId={THEORY_BLOCKS_DROPPABLE_ID}>
              {(provided) => (
                <Box {...provided.droppableProps} ref={provided.innerRef} sx={{ width: '100%' }}>
                  {activeChapterBlocks.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        minHeight: '240px',
                        padding: '24px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography>
                        <TranslateText i18nKey="createUnit.addingInfo" />
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {visibleBlocks.map((block, index) => (
                        <Draggable key={block.pseudoId} draggableId={block.pseudoId} index={index}>
                          {(dragProvided) => (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                            >
                              <TheoryBlockItem
                                block={block}
                                index={index}
                                domain={domain}
                                onToggle={(next) => {
                                  block.isOpen = next;
                                }}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {hasMoreBlocksToRender && (
                        <Box sx={{ padding: '0px 24px 16px', width: '100%' }}>
                          <Typography variant="caption" sx={{ opacity: 0.75 }}>
                            Загружаем блоки главы...
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Box>
          <Divider orientation="vertical" />
          <Box
            sx={{
              flex: '0.3',
              padding: '32px',
              height: '100%',
              overflowY: 'auto',
              background: Colors.bgElevated,
            }}
          >
            <Box sx={{ paddingBottom: '32px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  <TranslateText i18nKey="createUnit.chapters" />
                </Typography>
                <AddBox
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    domain.onAddChapter();
                  }}
                />
              </Box>
              {unitDomain.ui.unit.entity.theory.chapters.length !== 0 && (
                <Droppable droppableId={CHAPTERS_DROPPABLE_ID}>
                  {(provided) => (
                    <Box
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ maxHeight: '220px', overflowY: 'auto' }}
                    >
                      {unitDomain.ui.unit.entity.theory.chapters.map((item, index) => (
                        <Draggable key={item.pseudoId} draggableId={item.pseudoId} index={index}>
                          {(dragProvided, snapshot) => (
                            <DropItem
                              provided={dragProvided}
                              snapshot={snapshot}
                              domain={domain}
                              item={item}
                              index={index}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              )}
            </Box>
            {domain.ui.componentList.list.map((item, groupIndex) => {
              const componentDroppableId = `${COMPONENT_LIST_DROPPABLE_PREFIX}${groupIndex}`;
              return (
                <Box key={item.type + groupIndex} sx={{ marginBottom: '12px' }}>
                  <Typography variant="h6" sx={{ marginBottom: '12px' }}>
                    {item.type}
                  </Typography>
                  <Droppable droppableId={componentDroppableId} isDropDisabled={true}>
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                      >
                        {item.list.map((element, index) => {
                          const draggableId = `component-${groupIndex}-${index}-${element.block}`;
                          return (
                            <Draggable key={draggableId} draggableId={draggableId} index={index}>
                              {(dragProvided) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  onDoubleClick={() => {
                                    lastAddMethodRef.current = 'double_click';
                                    domain.onAddComponent(element);
                                  }}
                                  sx={{
                                    display: 'flex',
                                    gap: '8px',
                                    background: Colors.bgSurface,
                                    borderRadius: '8px',
                                    color: Colors.textPrimary,
                                    padding: '4px 0px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <MoreVert />
                                  <Typography>{element.name}</Typography>
                                </Box>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DragDropContext>
    </>
  );
});
